import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Chip,
} from "@mui/material";
import {
  PlayArrow,
  AcUnit,
  Compare,
  Error as ErrorIcon,
} from "@mui/icons-material";

const LoafLockWidget = () => {
  const [status, setStatus] = useState({
    model: "-",
    traces: 0,
    lock_entries: 0,
  });
  const [loading, setLoading] = useState({
    run: false,
    freeze: false,
    diff: false,
  });
  const [results, setResults] = useState({
    run: null,
    freeze: null,
    diff: null,
  });
  const [error, setError] = useState(null);

  const API_BASE = process.env.REACT_APP_LOCK_API;

  const fetchStatus = React.useCallback(async () => {
    try {
      setError(null);
      const response = await fetch(`${API_BASE}/status`);
      if (!response.ok) throw new Error("Failed to fetch status");
      const data = await response.json();
      setStatus(data);
    } catch (err) {
      setError(`Status fetch failed: ${err.message}`);
    }
  }, [API_BASE]);

  useEffect(() => {
    if (API_BASE) {
      fetchStatus();
    }
  }, [API_BASE, fetchStatus]);

  const runSample = async () => {
    setLoading((prev) => ({ ...prev, run: true }));
    setError(null);
    try {
      const response = await fetch(`${API_BASE}/demo/run`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      if (!response.ok) throw new Error("Failed to run sample");
      const data = await response.json();
      setResults((prev) => ({ ...prev, run: data }));
      await fetchStatus(); // Update status after run
    } catch (err) {
      setError(`Run sample failed: ${err.message}`);
    } finally {
      setLoading((prev) => ({ ...prev, run: false }));
    }
  };

  const freezeLockfile = async () => {
    setLoading((prev) => ({ ...prev, freeze: true }));
    setError(null);
    try {
      const response = await fetch(`${API_BASE}/demo/freeze`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      if (!response.ok) throw new Error("Failed to freeze lockfile");
      const data = await response.json();
      setResults((prev) => ({ ...prev, freeze: data }));
      await fetchStatus(); // Update status after freeze
    } catch (err) {
      setError(`Freeze failed: ${err.message}`);
    } finally {
      setLoading((prev) => ({ ...prev, freeze: false }));
    }
  };

  const diffForDrift = async () => {
    setLoading((prev) => ({ ...prev, diff: true }));
    setError(null);
    try {
      const response = await fetch(`${API_BASE}/demo/diff`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      if (!response.ok) throw new Error("Failed to diff for drift");
      const data = await response.json();
      setResults((prev) => ({ ...prev, diff: data }));
      await fetchStatus(); // Update status after diff
    } catch (err) {
      setError(`Diff failed: ${err.message}`);
    } finally {
      setLoading((prev) => ({ ...prev, diff: false }));
    }
  };

  if (!API_BASE) {
    return (
      <Alert severity="warning" sx={{ mb: 2 }}>
        LoafLock API not configured. Please set REACT_APP_LOCK_API in your .env
        file.
      </Alert>
    );
  }

  return (
    <Card
      sx={{
        backgroundColor: "rgba(255, 255, 255, 0.04)",
        backdropFilter: "blur(16px)",
        WebkitBackdropFilter: "blur(16px)",
        border: "1px solid rgba(255, 255, 255, 0.08)",
        borderRadius: 4,
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
        maxWidth: 600,
        mx: "auto",
      }}
    >
      <CardContent sx={{ p: 3 }}>
        {/* Status Line */}
        <Box sx={{ mb: 3, textAlign: "center" }}>
          <Typography
            variant="body2"
            sx={{
              color: "rgba(255, 255, 255, 0.6)",
              fontFamily: "monospace",
              fontSize: "0.875rem",
            }}
          >
            Model: {status.model} · Traces: {status.traces} · Lock entries:{" "}
            {status.lock_entries}
          </Typography>
        </Box>

        {/* Action Buttons */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Button
            variant="contained"
            startIcon={
              loading.run ? <CircularProgress size={16} /> : <PlayArrow />
            }
            onClick={runSample}
            disabled={loading.run || loading.freeze || loading.diff}
            sx={{
              flex: 1,
              backgroundColor: "#4a9eff",
              "&:hover": { backgroundColor: "#1a237e" },
            }}
          >
            Run Sample
          </Button>
          <Button
            variant="outlined"
            startIcon={
              loading.freeze ? <CircularProgress size={16} /> : <AcUnit />
            }
            onClick={freezeLockfile}
            disabled={loading.run || loading.freeze || loading.diff}
            sx={{
              flex: 1,
              borderColor: "rgba(255, 255, 255, 0.2)",
              color: "#ffffff",
              "&:hover": {
                borderColor: "rgba(255, 255, 255, 0.3)",
                backgroundColor: "rgba(255, 255, 255, 0.02)",
              },
            }}
          >
            Freeze Lockfile
          </Button>
          <Button
            variant="outlined"
            startIcon={
              loading.diff ? <CircularProgress size={16} /> : <Compare />
            }
            onClick={diffForDrift}
            disabled={loading.run || loading.freeze || loading.diff}
            sx={{
              flex: 1,
              borderColor: "rgba(255, 255, 255, 0.2)",
              color: "#ffffff",
              "&:hover": {
                borderColor: "rgba(255, 255, 255, 0.3)",
                backgroundColor: "rgba(255, 255, 255, 0.02)",
              },
            }}
          >
            Diff for Drift
          </Button>
        </Stack>

        {/* Error Display */}
        {error && (
          <Alert
            severity="error"
            icon={<ErrorIcon />}
            sx={{
              mb: 2,
              backgroundColor: "rgba(244, 67, 54, 0.1)",
              border: "1px solid rgba(244, 67, 54, 0.2)",
            }}
          >
            {error}
          </Alert>
        )}

        {/* Results Area */}
        <Box sx={{ mt: 3 }}>
          {/* Run Sample Result */}
          {results.run && (
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="caption"
                sx={{
                  color: "rgba(255, 255, 255, 0.6)",
                  textTransform: "uppercase",
                  fontWeight: 500,
                  mb: 1,
                  display: "block",
                }}
              >
                Last Run Result
              </Typography>
              <Box
                sx={{
                  p: 2,
                  backgroundColor: "rgba(255, 255, 255, 0.02)",
                  borderRadius: 2,
                  border: "1px solid rgba(255, 255, 255, 0.05)",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: "rgba(255, 255, 255, 0.9)",
                    fontFamily: "monospace",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {results.run.text || JSON.stringify(results.run, null, 2)}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Freeze Result */}
          {results.freeze && (
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="caption"
                sx={{
                  color: "rgba(255, 255, 255, 0.6)",
                  textTransform: "uppercase",
                  fontWeight: 500,
                  mb: 1,
                  display: "block",
                }}
              >
                Freeze Result
              </Typography>
              <Stack direction="row" spacing={2}>
                <Chip
                  label={`Added: ${results.freeze.added || 0}`}
                  size="small"
                  sx={{
                    backgroundColor: "#51c4a020",
                    border: "1px solid #51c4a040",
                    color: "#51c4a0",
                  }}
                />
                <Chip
                  label={`Total: ${results.freeze.total || 0}`}
                  size="small"
                  sx={{
                    backgroundColor: "#2196f320",
                    border: "1px solid #2196f340",
                    color: "#2196f3",
                  }}
                />
              </Stack>
            </Box>
          )}

          {/* Diff Result */}
          {results.diff && (
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="caption"
                sx={{
                  color: "rgba(255, 255, 255, 0.6)",
                  textTransform: "uppercase",
                  fontWeight: 500,
                  mb: 1,
                  display: "block",
                }}
              >
                Drift Analysis
              </Typography>
              <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
                <Chip
                  label={`Drift Count: ${results.diff.drift_count || 0}`}
                  size="small"
                  sx={{
                    backgroundColor:
                      results.diff.drift_count > 0 ? "#51c4a020" : "#51c4a020",
                    border: `1px solid ${
                      results.diff.drift_count > 0 ? "#51c4a040" : "#51c4a040"
                    }`,
                    color: results.diff.drift_count > 0 ? "#51c4a0" : "#51c4a0",
                  }}
                />
              </Stack>
              {results.diff.diffs && results.diff.diffs.length > 0 && (
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: "rgba(255, 255, 255, 0.02)",
                    borderRadius: 2,
                    border: "1px solid rgba(255, 255, 255, 0.05)",
                    maxHeight: 200,
                    overflow: "auto",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: "rgba(255, 255, 255, 0.8)",
                      fontFamily: "monospace",
                      fontSize: "0.75rem",
                    }}
                  >
                    {JSON.stringify(results.diff.diffs, null, 2)}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default LoafLockWidget;
